import React, { memo } from "react";
import { useController, useFormContext } from "react-hook-form";

import { cn } from "~/utils/tailwind";

export interface ISelectBoxProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  id?: string;
  name: string;
  dataBlockId?: string;
  label?: string;
  disabled?: boolean;
  hint?: string;
  className?: string;
  isRequired?: boolean;
  showLabel?: boolean;
  hidden?: boolean;
  autoComplete?: any;
  onFocus?: any;
  showErrorMessage?: boolean;
  options?: OptionsProps[];
  placeholder?: string;
}

interface OptionsProps {
  key: string | number;
  value: string;
}

const SelectBoxField = ({
  id,
  name,
  dataBlockId,
  label,
  disabled,
  hint,
  className,
  isRequired = false,
  showLabel = true,
  hidden,
  autoComplete,
  onFocus,
  showErrorMessage = true,
  options,
  placeholder,
  ...props
}: ISelectBoxProps) => {
  const { control } = useFormContext();
  const {
    field: { value, ref, ...inputProps },
    fieldState,
  } = useController({ control, name, defaultValue: "" });

  const isValidValue = options?.some(
    (opt) => String(opt.value).toLowerCase() === String(value).toLowerCase()
  );

  const safeValue = isValidValue ? value : "";

  return (
    <div className="relative h-full" hidden={hidden}>
      {showLabel && (
        <label
          htmlFor={name}
          className="absolute text-md text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/3 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 start-1 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto"
        >
          <span className={cn(fieldState.error && "text-red-700")}>
            {label} {isRequired && <span className="text-red-500">*</span>}
          </span>
        </label>
      )}
      <select
        id={id ?? name}
        aria-label={dataBlockId}
        className={cn(
          "block w-full h-full py-2 text-xs text-white-900 border border-gray-300 rounded bg-gray-50 focus:ring-blue-500 focus:border-blue-500",
          { "bg-gray-100 cursor-not-allowed": disabled },
          className,

          fieldState.error && "border-red-700 border-solid border-2",
          disabled && "cursor-not-allowed"
        )}
        disabled={disabled}
        autoComplete={autoComplete}
        onFocus={onFocus}
        value={safeValue}
        ref={ref}
        {...inputProps}
        {...props}
      >
        {/* <option value="">Select</option> */}
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options
          ?.sort((a, b) => a.value.localeCompare(b.value))
          .map((option, index: number) => (
            <option key={option.key ?? index} value={option.key}>
              {option.value}
            </option>
          ))}
      </select>
      {fieldState?.error?.message && showErrorMessage && (
        <span className="text-red-700 px-1 text-xs">
          {fieldState?.error?.message}
        </span>
      )}
      {hint && <span className="px-2 text-xs">{hint}</span>}
    </div>
  );
};

export default memo(SelectBoxField);
